import { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import {
  calculateDistance,
  convertToMapboxFormat,
} from "../utils/wayfinderUtils";

export const useWayfinderLocation = (bibData) => {
  const [userLocation, setUserLocation] = useState([151.2072222, -33.8402778]); // Default location - Mapbox uses [lng, lat]
  const [customLocation, setCustomLocation] = useState("");
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isLocationTrackingActive, setIsLocationTrackingActive] =
    useState(false);

  // Define getCurrentLocation function
  const getCurrentLocation = useCallback(() => {
    setIsLoadingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = [
            position.coords.longitude, // Mapbox uses [lng, lat]
            position.coords.latitude,
          ];
          setUserLocation(newLocation);
          setIsLoadingLocation(false);

          // Update initial distance to start point if BIB data is available
          if (bibData && bibData.routeStartCoordinates) {
            const routeStartCoords = convertToMapboxFormat(
              bibData.routeStartCoordinates
            );
            if (routeStartCoords) {
              const currentDistance = calculateDistance(
                newLocation[1], // lat
                newLocation[0], // lng
                routeStartCoords[1], // route start lat
                routeStartCoords[0] // route start lng
              );
              sessionStorage.setItem(
                "initialDistanceToStart",
                currentDistance.toString()
              );
            }
          }
        },
        (error) => {
          console.error("Error getting location:", error);

          // Handle specific error codes
          switch (error.code) {
            case 1: // PERMISSION_DENIED
              toast.error(
                "Location access denied. Falling back to simulated location."
              );
              // Fall back to simulated location
              setUserLocation([151.2072222, -33.8402778]);
              setIsLoadingLocation(false);
              return; // Exit early to prevent fallback
            case 2: // POSITION_UNAVAILABLE
              // Auto-retry for position unavailable
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  getCurrentLocation();
                }
              }, 3000);
              return; // Exit early to prevent fallback
            case 3: // TIMEOUT
              console.log(
                "⏰ [LOCATION] getCurrentLocation timeout, retrying..."
              );
              // Auto-retry for timeout with progressive delay
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  console.log("🔄 [LOCATION] Retrying getCurrentLocation...");
                  getCurrentLocation();
                }
              }, 3000);
              return; // Exit early to prevent fallback
            default:
              // Auto-retry for unknown errors
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  getCurrentLocation();
                }
              }, 5000);
              return; // Exit early to prevent fallback
          }
        },
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 30000 }
      );
    } else {
      setUserLocation([151.2072222, -33.8402778]);
      setIsLoadingLocation(false);
    }
  }, [bibData, isLocationTrackingActive]);

  // Handle location type change
  const handleLocationTypeChange = useCallback((e) => {
    setCustomLocation(e.target.value);
  }, []);

  // Geocoding function to search for locations
  const searchLocation = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearchingLocation(true);
    try {
      // Using OpenStreetMap Nominatim API for geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5&addressdetails=1&countrycodes=au`
      );
      const data = await response.json();

      const results = data.map((item) => ({
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        type: item.type,
      }));

      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error("Error searching location:", error);
      setSearchResults([]);
    } finally {
      setIsSearchingLocation(false);
    }
  }, []);

  // Handle selecting a search result
  const handleLocationSelect = useCallback(
    (result) => {
      setUserLocation([result.lon, result.lat]); // Mapbox uses [lng, lat]
      setCustomLocation(result.display_name);
      setShowSearchResults(false);
      setSearchResults([]);

      // Update initial distance to start point if BIB data is available
      if (bibData && bibData.routeStartCoordinates) {
        const routeStartCoords = convertToMapboxFormat(
          bibData.routeStartCoordinates
        );
        if (routeStartCoords) {
          const currentDistance = calculateDistance(
            result.lat, // lat
            result.lon, // lng
            routeStartCoords[1], // route start lat
            routeStartCoords[0] // route start lng
          );
          sessionStorage.setItem(
            "initialDistanceToStart",
            currentDistance.toString()
          );
        }
      }
    },
    [bibData]
  );

  // Handle starting location tracking
  const handleStartLocationTracking = useCallback(
    (retryCount = 0, onLocationUpdate) => {
      console.log(
        `🚀 [TRACKING] Starting location tracking (attempt ${
          retryCount + 1
        }/5)...`
      );

      // If already tracking, stop tracking
      if (isLocationTrackingActive) {
        if (window.locationWatchId) {
          navigator.geolocation.clearWatch(window.locationWatchId);
          window.locationWatchId = null;
        }

        setIsLocationTrackingActive(false);
        return;
      }

      // Set loading state for better UX
      setIsLoadingLocation(true);

      // Prevent infinite retries
      if (retryCount >= 5) {
        console.log(
          "❌ [LOCATION] Max retries reached, falling back to simulated location"
        );
        setIsLocationTrackingActive(false);
        setUseCurrentLocation(false);
        setUserLocation([151.2072222, -33.8402778]);

        // Show user-friendly message
        if (
          window.confirm(
            "Unable to get your location after multiple attempts. Would you like to use a default location near the route start? Click 'OK' to continue with default location."
          )
        ) {
          console.log(
            "✅ [LOCATION] User chose to continue with default location"
          );
        } else {
          console.log("❌ [LOCATION] User cancelled location setup");
        }
        setIsLoadingLocation(false); // Clear loading state
        return;
      }

      // Check if geolocation is supported
      if (!navigator.geolocation) {
        toast.error(
          "Your browser doesn't support location services. Please use a modern browser."
        );
        return;
      }

      // Enable current location tracking
      setUseCurrentLocation(true);
      setIsLocationTrackingActive(true);

      // First, try to get current position to trigger permission popup
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const initialLocation = [
            position.coords.longitude,
            position.coords.latitude,
          ];
          setUserLocation(initialLocation);
          setIsLoadingLocation(false); // Clear loading state

          // Now start continuous tracking
          startContinuousTracking();
        },
        (error) => {
          console.error(
            "❌ [TRACKING] Initial location request failed:",
            error
          );

          // Handle specific error codes for initial request
          switch (error.code) {
            case 1: // PERMISSION_DENIED
              toast.error(
                "Location access denied. Auto-retrying in 3 seconds..."
              );
              // Auto-retry for permission denied
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  console.log(
                    "🔄 [LOCATION] Retrying after permission denied..."
                  );
                  handleStartLocationTracking(retryCount + 1);
                }
              }, 3000);
              return;
            case 2: // POSITION_UNAVAILABLE
              console.log(
                "📍 [LOCATION] Location unavailable - device cannot determine position"
              );
              console.log("🔄 [LOCATION] Auto-retrying in 3 seconds...");
              // Auto-retry for position unavailable
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  console.log(
                    "🔄 [LOCATION] Retrying initial location request..."
                  );
                  handleStartLocationTracking(retryCount + 1);
                }
              }, 3000);
              return; // Exit early to prevent fallback
            case 3: // TIMEOUT
              console.log(
                `⏰ [LOCATION] Location request timed out (attempt ${
                  retryCount + 1
                }/5)`
              );

              // Progressive delay based on retry count
              const delay = Math.min(2000 * (retryCount + 1), 10000);
              console.log(
                `🔄 [LOCATION] Auto-retrying in ${delay / 1000} seconds...`
              );

              // Auto-retry for timeout with progressive backoff
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  console.log(
                    `🔄 [LOCATION] Retrying initial location request (attempt ${
                      retryCount + 2
                    }/5)...`
                  );
                  handleStartLocationTracking(retryCount + 1);
                } else {
                  console.log(
                    "❌ [LOCATION] Location tracking was stopped, not retrying"
                  );
                  setIsLoadingLocation(false); // Clear loading state if tracking stopped
                }
              }, delay);
              return; // Exit early to prevent fallback
            default:
              console.log(
                "❓ [LOCATION] Unknown location error:",
                error.message
              );
              console.log("🔄 [LOCATION] Auto-retrying in 5 seconds...");
              // Auto-retry for unknown errors
              setTimeout(() => {
                if (isLocationTrackingActive) {
                  console.log(
                    "🔄 [LOCATION] Retrying initial location request..."
                  );
                  handleStartLocationTracking(retryCount + 1);
                }
              }, 5000);
              return; // Exit early to prevent fallback
          }
        },
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 30000 }
      );

      // Function to start continuous tracking
      const startContinuousTracking = () => {
        if (navigator.geolocation) {
          // Clear any existing watch
          if (window.locationWatchId) {
            navigator.geolocation.clearWatch(window.locationWatchId);
          }

          // Start watching position with high accuracy
          window.locationWatchId = navigator.geolocation.watchPosition(
            (position) => {
              const newLocation = [
                position.coords.longitude, // Mapbox uses [lng, lat]
                position.coords.latitude,
              ];

              setUserLocation(newLocation);

              // Call the callback if provided (for map centering logic)
              if (onLocationUpdate) {
                onLocationUpdate(newLocation);
              }
            },
            (error) => {
              console.error("❌ [TRACKING] Location tracking error:", error);

              // Handle specific error codes
              switch (error.code) {
                case 1: // PERMISSION_DENIED
                  toast.error(
                    "Location access denied. Stopping tracking and falling back to simulated location."
                  );
                  // Stop tracking and fall back to simulated location
                  setIsLocationTrackingActive(false);
                  setUseCurrentLocation(false);
                  setUserLocation([151.2072222, -33.8402778]); // Default simulated location
                  return; // Exit early to prevent fallback
                case 2: // POSITION_UNAVAILABLE
                  // Auto-retry for position unavailable (user might have moved)
                  setTimeout(() => {
                    if (isLocationTrackingActive) {
                      startContinuousTracking();
                    }
                  }, 3000);
                  return; // Exit early to prevent fallback
                case 3: // TIMEOUT
                  console.log(
                    "⏰ [TRACKING] Continuous tracking timeout, retrying..."
                  );
                  // Auto-retry for timeout (network or GPS might be slow)
                  setTimeout(() => {
                    if (isLocationTrackingActive) {
                      console.log(
                        "🔄 [TRACKING] Restarting continuous tracking..."
                      );
                      startContinuousTracking();
                    }
                  }, 3000);
                  return; // Exit early to prevent fallback
                default:
                  // Auto-retry for unknown errors
                  setTimeout(() => {
                    if (isLocationTrackingActive) {
                      startContinuousTracking();
                    }
                  }, 5000);
                  return; // Exit early to prevent fallback
              }
            },
            {
              enableHighAccuracy: true, // More accurate, better for navigation
              timeout: 30000, // Longer timeout (30 seconds) for better reliability
              maximumAge: 30000, // Accept locations up to 30 seconds old
            }
          );

          // Location tracking started successfully
        } else {
          // Fall back to one-time location fetch
          getCurrentLocation();
        }
      };
    },
    [isLocationTrackingActive, getCurrentLocation]
  );

  // Cleanup location tracking on component unmount
  useEffect(() => {
    return () => {
      if (window.locationWatchId) {
        navigator.geolocation.clearWatch(window.locationWatchId);
        window.locationWatchId = null;
        setIsLocationTrackingActive(false);
      }
    };
  }, []);

  // Handle location search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customLocation.trim() && !useCurrentLocation) {
        searchLocation(customLocation);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [customLocation, useCurrentLocation, searchLocation]);

  return {
    userLocation,
    setUserLocation,
    customLocation,
    setCustomLocation,
    useCurrentLocation,
    setUseCurrentLocation,
    isLoadingLocation,
    setIsLoadingLocation,
    isSearchingLocation,
    searchResults,
    showSearchResults,
    isLocationTrackingActive,
    setIsLocationTrackingActive,
    getCurrentLocation,
    handleLocationTypeChange,
    searchLocation,
    handleLocationSelect,
    handleStartLocationTracking,
  };
};
